import {useCallback,useEffect,useId,useMemo,useRef,useState,type RefObject} from 'react'
import type {Me} from '@briary/shared'
import {Modal} from '../../ui'
import type {HelpTopic} from './catalog'
import {helpTopicsFor,nextHelpFocus,type HelpArea,type HelpEnvironment,type HelpLocation} from './topics'
import {HelpVideo} from './HelpVideo'
import {HelpKeyboardBoundary} from './HelpKeyboardBoundary'

export function HelpGuide({topic,open,onToggle}:{topic:HelpTopic;open:boolean;onToggle:()=>void}){
  const region=useId()
  return <article className="context-help-guide">
    <h4><button type="button" className="context-help-topic" aria-expanded={open} aria-controls={region} onClick={onToggle}>
      <span>{topic.title}<small>{topic.category}</small></span><span aria-hidden="true">{open?'−':'+'}</span>
    </button></h4>
    <div id={region} className="context-help-steps" hidden={!open}>
      <ol>{topic.steps.map((step,index)=><li key={index}>{step}</li>)}</ol>
      {topic.notice&&<p className="context-help-note">{topic.notice}</p>}
      {open&&<HelpVideo key={topic.id} id={topic.id} title={topic.title}/>}
    </div>
  </article>
}

export function HelpDialog({me,area,location,environment,onClose,returnFocus}: // No data mutations or navigation actions live in this dialog.
  {me:Me;area:HelpArea;location:HelpLocation;environment:HelpEnvironment;onClose:()=>void;returnFocus:RefObject<HTMLButtonElement|null>}){
  const title=useId(),description=useId(),searchId=useId()
  const [query,setQuery]=useState('')
  const topics=useMemo(()=>helpTopicsFor(me,area,location,query,environment),[me,area,location,query,environment])
  const [selected,setSelected]=useState<string|null>(()=>(topics.current[0]??topics.other[0])?.id??null)
  const root=useRef<HTMLDivElement>(null),search=useRef<HTMLInputElement>(null)
  const closeRef=useRef(onClose)
  useEffect(()=>{closeRef.current=onClose},[onClose])
  const close=useCallback(()=>closeRef.current(),[])
  useEffect(()=>{
    const previousOverflow=document.body.style.overflow
    document.body.style.overflow='hidden';search.current?.focus()
    const key=(event:KeyboardEvent)=>{
      if(event.key==='Escape'){event.preventDefault();event.stopImmediatePropagation();close();return}
      if(event.key!=='Tab'||!root.current)return
      const elements=Array.from(root.current.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),a[href],video[controls],[tabindex="0"]')).filter(node=>node.getClientRects().length>0)
      const current=elements.indexOf(document.activeElement as HTMLElement)
      if(current<0||current===(event.shiftKey?0:elements.length-1)){
        const next=nextHelpFocus(current,elements.length,event.shiftKey)
        if(next>=0){event.preventDefault();elements[next].focus()}
      }
    }
    const focus=(event:FocusEvent)=>{if(root.current&&!root.current.contains(event.target as Node))search.current?.focus()}
    document.addEventListener('keydown',key,true);document.addEventListener('focusin',focus)
    const trigger=returnFocus.current
    return ()=>{
      document.removeEventListener('keydown',key,true);document.removeEventListener('focusin',focus)
      document.body.style.overflow=previousOverflow
      if(trigger?.isConnected)trigger.focus()
    }
  },[close,returnFocus])
  const group=(heading:string,list:readonly HelpTopic[])=><section className="context-help-group" aria-label={heading}>
    <h3>{heading}</h3>
    {list.map(topic=><HelpGuide key={topic.id} topic={topic} open={selected===topic.id} onToggle={()=>setSelected(current=>current===topic.id?null:topic.id)}/>)}
  </section>
  return <Modal className="context-help-modal" onClose={close} labelledBy={title} describedBy={description}>
    <HelpKeyboardBoundary containerRef={root}>
      <header className="context-help-header"><div><h2 id={title}>Help</h2><p id={description}>Step-by-step guides for your {area==='portal'?'manager portal':'staff workspace'}.</p></div>
        <button type="button" className="btn btn-ghost" aria-label="Close help" onClick={close}>Close <span aria-hidden="true">×</span></button>
      </header>
      <form role="search" className="context-help-search" onSubmit={event=>event.preventDefault()}>
        <label htmlFor={searchId}>Search help</label>
        <div className="row"><input ref={search} id={searchId} className="input" type="search" maxLength={160} value={query} placeholder="Tasks, EPC requests, invoices…" onChange={event=>{setQuery(event.target.value);setSelected(null)}}/>
          {query&&<button type="button" className="btn btn-sm" onClick={()=>{setQuery('');search.current?.focus()}}>Clear</button>}
        </div>
        <p role="status" aria-live="polite" className="muted">{topics.current.length+topics.other.length} guides available</p>
      </form>
      <div className="context-help-content">
        {topics.current.length>0&&group('For this page',topics.current)}
        {topics.other.length>0&&group('Browse all help',topics.other)}
        {!topics.current.length&&!topics.other.length&&<p>No matching guides. Try a shorter search.</p>}
      </div>
    </HelpKeyboardBoundary>
  </Modal>
}
